import React, { useEffect, useMemo, useRef, useState } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { Box, Typography } from "@mui/material";
import { DARK_NAVY_LIGHT, INPUT_BORDER_FOCUS, PROFILE_FONT } from "../../constants/layout";

const ICON_BLUE = INPUT_BORDER_FOCUS;

const formatAxisTick = (value: number) => {
  if (value >= 1e7) return `₹${(value / 1e7).toFixed(1)}Cr`;
  if (value >= 1e5) return `₹${(value / 1e5).toFixed(1)}L`;
  if (value >= 1e3) return `₹${(value / 1e3).toFixed(0)}K`;
  return `₹${Math.round(value)}`;
};

interface BasicBarsProps {
  data: Array<{ name: string; value: number }>;
  color?: string;
  height?: number;
  yAxisLabel?: string;
}

const BasicBars: React.FC<BasicBarsProps> = ({
  data,
  color = ICON_BLUE,
  height = 400,
  yAxisLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);

  const maxValue = useMemo(
    () => data.reduce((max, item) => Math.max(max, item.value), 0),
    [data]
  );

  const leftMargin = useMemo(() => {
    const tickSample = formatAxisTick(maxValue);
    return Math.max(52, tickSample.length * 7 + 16);
  }, [maxValue]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {yAxisLabel ? (
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: 12,
            fontWeight: 500,
            color: DARK_NAVY_LIGHT,
            mb: 0.75,
            pl: 0.5,
          }}
        >
          {yAxisLabel}
        </Typography>
      ) : null}
      <BarChart
        margin={{ top: 12, right: 16, left: leftMargin, bottom: 72 }}
        xAxis={[
          {
            scaleType: "band",
            data: data.map((item) => item.name),
            tickLabelStyle: {
              fontFamily: PROFILE_FONT,
              fontSize: 11,
              fill: DARK_NAVY_LIGHT,
            },
          },
        ]}
        yAxis={[
          {
            tickNumber: 5,
            valueFormatter: formatAxisTick,
            tickLabelStyle: {
              fontFamily: PROFILE_FONT,
              fontSize: 11,
              fill: DARK_NAVY_LIGHT,
            },
          },
        ]}
        series={[
          {
            data: data.map((item) => item.value),
            color,
          },
        ]}
        width={width}
        height={height}
        sx={{
          "& .MuiChartsAxis-line": { stroke: "#E8E8E8" },
          "& .MuiChartsAxis-tick": { stroke: "#E8E8E8" },
          "& .MuiChartsGrid-line": { stroke: "#F5F5F5" },
          "& .MuiBarElement-root": { rx: 6 },
        }}
      />
    </Box>
  );
};

export default BasicBars;
